import { __awaiter, __generator } from "tslib";
import { createDarkTheme, createLightTheme, webDarkTheme, webLightTheme, } from '@fluentui/react-components';
import { OfficeThemeMode, } from '@office-start/theming-types';
// How many themes to cache
// TODO: Should this be configurable?
export var cacheMaxSize = 10;
// Queue of cached themes to handle eviction
var themeQueue = [];
// Cache of Fluent themes
var themeCache = new Map();
export function createFluentTheme(theme) {
    return __awaiter(this, void 0, void 0, function () {
        var themeKey, cachedTheme, fluentTheme;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    themeKey = createCacheKey(theme);
                    cachedTheme = getCachedTheme(themeKey);
                    if (cachedTheme) {
                        return [2 /*return*/, cachedTheme];
                    }
                    return [4 /*yield*/, createFluentThemeInternal(theme)];
                case 1:
                    fluentTheme = _a.sent();
                    cacheTheme(themeKey, fluentTheme);
                    return [2 /*return*/, fluentTheme];
            }
        });
    });
}
function getCachedTheme(themeKey) {
    var cachedTheme = themeCache.get(themeKey);
    if (cachedTheme) {
        // Move latest used theme to end of queue
        var queueIndex = themeQueue.indexOf(themeKey);
        themeQueue.splice(queueIndex, 1);
        themeQueue.push(themeKey);
    }
    return cachedTheme;
}
function cacheTheme(themeKey, theme) {
    if (themeCache.has(themeKey)) {
        return;
    }
    themeQueue.push(themeKey);
    themeCache.set(themeKey, theme);
    if (themeQueue.length > cacheMaxSize) {
        var evictedTheme = themeQueue.shift();
        if (evictedTheme) {
            themeCache.delete(evictedTheme);
        }
    }
}
function createCacheKey(theme) {
    return "M".concat(theme.mode, "L").concat(paletteToString(theme.lightBrandColors), "D").concat(paletteToString(theme.darkBrandColors));
}
function paletteToString(palette) {
    if (!palette) {
        return '';
    }
    return (palette.themeDark +
        palette.themeDarkAlt +
        palette.themeDarker +
        palette.themeLight +
        palette.themeLight +
        palette.themeLighter +
        palette.themeLighterAlt +
        palette.themePrimary +
        palette.themeSecondary +
        palette.themeTertiary);
}
function createFluentThemeInternal(theme) {
    return __awaiter(this, void 0, void 0, function () {
        var fluentTheme, brandVariants, _a, createHighContrastTheme;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    brandVariants = getBrandVariants(theme);
                    _a = theme.mode;
                    switch (_a) {
                        case OfficeThemeMode.dark: return [3 /*break*/, 1];
                        case OfficeThemeMode.highContrast: return [3 /*break*/, 2];
                    }
                    return [3 /*break*/, 4];
                case 1:
                    fluentTheme = brandVariants
                        ? createDarkTheme(brandVariants)
                        : webDarkTheme;
                    return [3 /*break*/, 5];
                case 2: return [4 /*yield*/, import('../lazy-modules/fluentHighContrastTheme')];
                case 3:
                    createHighContrastTheme = (_b.sent()).createHighContrastTheme;
                    fluentTheme = createHighContrastTheme();
                    return [3 /*break*/, 5];
                case 4:
                    fluentTheme = brandVariants
                        ? createLightTheme(brandVariants)
                        : webLightTheme;
                    return [3 /*break*/, 5];
                case 5: return [2 /*return*/, applyTokenOverrides(fluentTheme, brandVariants, theme)];
            }
        });
    });
}
// Overrides default fluent token mapping based on corrections for interaction states in Office brands: https://www.figma.com/file/wPgC5WVyqqXRerAhj5qf2L/Office-X-Fluent?node-id=4783%3A334227
// TODO: Remove overrides once fluent tokens and exported theme are updated to support interaction states
function applyTokenOverrides(fluentTheme, brandPalette, officeTheme) {
    if (!officeTheme.overrideTokenMapping || !brandPalette) {
        return fluentTheme;
    }
    if (officeTheme.themeId === 'harmony') {
        fluentTheme.colorBrandBackgroundInvertedPressed = brandPalette[130];
        fluentTheme.colorBrandForegroundOnLightPressed = brandPalette[40];
        switch (officeTheme.mode) {
            case OfficeThemeMode.dark:
                fluentTheme.colorBrandBackground2 = brandPalette[20];
                fluentTheme.colorBrandBackgroundHover = brandPalette[100];
                fluentTheme.colorBrandForeground2 = brandPalette[120];
                fluentTheme.colorBrandForegroundInverted = brandPalette[100];
                fluentTheme.colorBrandForegroundInvertedPressed =
                    brandPalette[40];
                fluentTheme.colorBrandForegroundLinkHover = brandPalette[120];
                fluentTheme.colorBrandForegroundLinkSelected =
                    brandPalette[110];
                fluentTheme.colorBrandForegroundOnLight = brandPalette[100];
                fluentTheme.colorCompoundBrandBackgroundHover =
                    brandPalette[120];
                fluentTheme.colorCompoundBrandForeground1Hover =
                    brandPalette[120];
                fluentTheme.colorCompoundBrandStrokeHover = brandPalette[120];
                break;
            case OfficeThemeMode.default:
                fluentTheme.colorBrandForegroundInvertedPressed =
                    brandPalette[90];
                fluentTheme.colorBrandStroke2 = brandPalette[140];
                fluentTheme.colorCompoundBrandBackgroundPressed =
                    brandPalette[40];
                fluentTheme.colorCompoundBrandStrokePressed = brandPalette[40];
                break;
        }
        return fluentTheme;
    }
    switch (officeTheme.mode) {
        case OfficeThemeMode.dark:
            fluentTheme.colorBrandForegroundOnLightHover = brandPalette[60];
            fluentTheme.colorBrandForegroundOnLightPressed = brandPalette[30];
            fluentTheme.colorBrandForegroundOnLightSelected = brandPalette[50];
            fluentTheme.colorBrandBackground = brandPalette[60];
            fluentTheme.colorBrandBackgroundPressed = brandPalette[30];
            fluentTheme.colorBrandBackgroundSelected = brandPalette[50];
            fluentTheme.colorBrandBackground2 = brandPalette[30];
            break;
        case OfficeThemeMode.default:
            fluentTheme.colorNeutralForeground2BrandPressed = brandPalette[60];
            fluentTheme.colorNeutralForeground3BrandPressed = brandPalette[60];
            fluentTheme.colorBrandForegroundLink = brandPalette[60];
            fluentTheme.colorBrandForegroundLinkHover = brandPalette[50];
            fluentTheme.colorBrandForegroundLinkPressed = brandPalette[30];
            fluentTheme.colorBrandForegroundLinkSelected = brandPalette[60];
            fluentTheme.colorCompoundBrandForeground1Hover = brandPalette[60];
            fluentTheme.colorCompoundBrandForeground1Pressed = brandPalette[50];
            fluentTheme.colorBrandForeground2 = brandPalette[60];
            fluentTheme.colorBrandForegroundOnLightHover = brandPalette[60];
            fluentTheme.colorBrandForegroundOnLightPressed = brandPalette[30];
            fluentTheme.colorBrandForegroundOnLightSelected = brandPalette[50];
            fluentTheme.colorBrandBackgroundHover = brandPalette[60];
            fluentTheme.colorBrandBackgroundPressed = brandPalette[30];
            fluentTheme.colorBrandBackgroundSelected = brandPalette[50];
            fluentTheme.colorCompoundBrandBackgroundHover = brandPalette[60];
            fluentTheme.colorCompoundBrandBackgroundPressed = brandPalette[50];
            fluentTheme.colorCompoundBrandStrokeHover = brandPalette[60];
            fluentTheme.colorCompoundBrandStrokePressed = brandPalette[50];
            break;
    }
    return fluentTheme;
}
function getBrandVariants(theme) {
    if (!theme.lightBrandColors) {
        return undefined;
    }
    if (!theme.darkBrandColors) {
        // Some themes (tenant theme for ex) don't have both a light and dark palette.
        // Historically we've shown the default orange theme in dark mode, so return undefined in dark mode.
        // For light mode, we need to construct a custom palette as the light design tokens are mapped from a combination of light and dark palettes normally.
        if (theme.mode === OfficeThemeMode.dark) {
            return undefined;
        }
        return {
            10: theme.lightBrandColors.themeDarker,
            20: theme.lightBrandColors.themeDarker,
            30: theme.lightBrandColors.themeDarker,
            40: theme.lightBrandColors.themeDarker,
            50: theme.lightBrandColors.themeDarker,
            60: theme.lightBrandColors.themeDark,
            70: theme.lightBrandColors.themeDarkAlt,
            80: theme.lightBrandColors.themePrimary,
            90: theme.lightBrandColors.themeSecondary,
            100: theme.lightBrandColors.themeSecondary,
            110: theme.lightBrandColors.themeTertiary,
            120: theme.lightBrandColors.themeTertiary,
            130: theme.lightBrandColors.themeTertiary,
            140: theme.lightBrandColors.themeTertiary,
            150: theme.lightBrandColors.themeLight,
            160: theme.lightBrandColors.themeLighter,
        };
    }
    return {
        10: theme.darkBrandColors.themeLighter,
        20: theme.darkBrandColors.themeLight,
        30: theme.lightBrandColors.themeDarker,
        40: theme.darkBrandColors.themeTertiary,
        50: theme.lightBrandColors.themeDark,
        60: theme.lightBrandColors.themeDarkAlt,
        70: theme.overridePaletteMapping
            ? theme.lightBrandColors.themeSecondary
            : theme.darkBrandColors.themeSecondary,
        80: theme.lightBrandColors.themePrimary,
        90: theme.overridePaletteMapping
            ? theme.darkBrandColors.themeSecondary
            : theme.lightBrandColors.themeSecondary,
        100: theme.darkBrandColors.themePrimary,
        110: theme.darkBrandColors.themeDarkAlt,
        120: theme.darkBrandColors.themeDark,
        130: theme.lightBrandColors.themeTertiary,
        140: theme.darkBrandColors.themeDarker,
        150: theme.lightBrandColors.themeLight,
        160: theme.lightBrandColors.themeLighter,
    };
}
//# sourceMappingURL=createFluentTheme.js.map